import React from "react";
import { TextField } from "@mui/material";
import { Controller, Control } from "react-hook-form";
import { FieldsResponse } from "@/modules/common/dtos/index.v2";
import { isAfter, isBefore } from "date-fns";

interface TimePickerRendererProps {
  fullName: string;
  control: Control;
  fieldRules: Record<string, any>;
  status_form_field: FieldsResponse;
  commonProps: Record<string, any>;
}

const TimePickerRenderer: React.FC<TimePickerRendererProps> = ({
  fullName,
  control,
  fieldRules,
  status_form_field,
  commonProps,
}) => {
  const convertTo24HourFormat = (time12h: string) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "pm") {
      hours = (parseInt(hours, 10) + 12).toString();
    }

    return `${hours}:${minutes}`;
  };

  const validateDate = (value: any): string | true => {
    const dateValue = convertToTime(value);
    if (!dateValue) {
      return "Valid time is required";
    }

    if (isNaN(dateValue.getTime())) {
      return "Valid time is required";
    }

    // Convert maxTime string to Date for comparison
    if (fieldRules.maxTime) {
      const [maxHours, maxMinutes] = fieldRules.maxTime.split(":").map(Number);
      const maxTimeDate = new Date();
      maxTimeDate.setHours(maxHours, maxMinutes, 0, 0);

      if (isAfter(dateValue, maxTimeDate)) {
        const errorMessage = `Time must be before ${convertTo24HourFormat(
          fieldRules.maxTime
        )}`;
        return errorMessage;
      }
    }

    // Convert minTime string to Date for comparison
    if (fieldRules.minTime) {
      const [minHours, minMinutes] = fieldRules.minTime.split(":").map(Number);
      const minTimeDate = new Date();
      minTimeDate.setHours(minHours, minMinutes, 0, 0);

      if (isBefore(dateValue, minTimeDate)) {
        const errorMessage = `Time must be after ${convertTo24HourFormat(
          fieldRules.minTime
        )}`;
        return errorMessage;
      }
    }

    return true;
  };

  const convertToTime = (newValue: string | null) => {
    if (!newValue) {
      return null;
    }

    const [hours, minutes] = newValue.split(":").map(Number);
    const dateValue = new Date();
    dateValue.setHours(hours, minutes, 0, 0);

    if (isNaN(dateValue.getTime())) {
      return null;
    }

    return dateValue;
  };

  return (
    <Controller
      name={fullName}
      control={control}
      rules={{
        validate: validateDate,
        required: fieldRules.required ? "This field is required" : false,
      }}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const defaultValue = status_form_field.field.defaultValue
          ? convertTo24HourFormat(status_form_field.field.defaultValue)
          : "";

        const mappedValue = value || defaultValue;

        return (
          <TextField
            {...commonProps}
            label={status_form_field.field.label || ""}
            type="time"
            onChange={(e) => {
              const inputValue = e.target.value;
              onChange(inputValue);
            }}
            value={mappedValue || null}
            error={!!error}
            helperText={error?.message || ""}
          />
        );
      }}
    />
  );
};

export default TimePickerRenderer;
